import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTopology, useSnapshot, useRecent } from "@/store/site";
import { useUiPref } from "@/store/uiPrefs";
import { PageShell } from "./PageShell";
import { SiteFlowDiagram } from "@/components/scada/SiteFlowDiagram";
import { PowerSparkChart } from "@/components/charts/PowerSparkChart";
import { Card, CardHeader, CardTitle, GranBadge } from "@/components/ui/Card";
import { HelpHint } from "@/components/ui/HelpHint";
import { useGlossary } from "@/lib/glossary";
import { useT } from "@/lib/i18n";
import { signedKW, formatHz, formatV, unsignedKW, direction, bessDirection, isIdlePower } from "@/lib/format";
import { carryTo } from "@/lib/carryParams";
import { Activity, ArrowRight, BatteryCharging, Cable, House, Sun } from "lucide-react";
import { cn } from "@/lib/cn";

/** Full-screen flow diagram + per-edge magnitudes + recent timeline. */
export default function EnergyFlowPage() {
  const topology = useTopology();
  const snap = useSnapshot();
  const recent = useRecent();
  const t = useT();
  const g = useGlossary();
  const hasPv = topology.smartmeters.some((m) => m.role === "pv");
  // SCADA diagram density (PERS-09): Detailed by default → today's exact view.
  const [scadaDetail, setScadaDetail] = useUiPref<boolean>("scada.detail", true);

  // Live directions drive each card's tone + arrow/sub-label (OVW-09):
  // an exporting site must not wear the import colour, and a PV-surplus
  // (negative) load must not read as a positive flow TO the loads.
  const gridDir = direction(snap.grid.active_W);
  const loadDir = direction(snap.building_load_W);

  // "Open in Historical →" (LINK-08): the chart below plots the fixed live
  // 60-min buffer, so the hand-off window is simply now−1h → now at raw
  // granularity. Recomputed on every render — the page re-renders with each
  // live snapshot, so the window stays current. Explicit overrides win over
  // any carried range params.
  const nowTs = Date.now();
  const openInHistoricalHref = carryTo("/historical", {
    keys: hasPv
      ? "grid_W,battery/aggregation/power,pv_power_W"
      : "grid_W,battery/aggregation/power",
    start: String(nowTs - 3_600_000),
    end: String(nowTs),
    gran: "raw",
    preset: "custom",
  });

  // N20 — the per-edge cards now drill exactly like the SiteFlowDiagram nodes
  // above them, so the page is internally consistent (NOT RightNowStrip's
  // targets — the Loads card must go to Historical, never self-link to /flow).
  // When the URL carries no window yet, the Historical drills fall back to a
  // 24h preset so the destination doesn't open on its own default (mirrors the
  // diagram's drillToHistorical). The Loads key set matches the diagram's
  // load-node handler (grid + battery + pv when present).
  const hasWindow = new URLSearchParams(window.location.search).has("start");
  const presetFallback = hasWindow ? {} : { preset: "24h" };
  const gridEdgeHref = carryTo("/historical", { keys: "grid_W", ...presetFallback });
  const pvEdgeHref = carryTo("/historical", { keys: "pv_power_W", ...presetFallback });
  const bessEdgeHref = carryTo("/bess");
  const loadsEdgeHref = carryTo("/historical", {
    keys: hasPv
      ? "grid_W,battery/aggregation/power,pv_power_W"
      : "grid_W,battery/aggregation/power",
    ...presetFallback,
  });

  return (
    <PageShell
      title={t("nav.energyFlow")}
      caption={t("monitor.flowPage.caption")}
    >
      <Card>
        <div className="p-3">
          <SiteFlowDiagram topology={topology} snap={snap} variant="full" detail={scadaDetail} onDetailChange={setScadaDetail} />
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        <FlowEdge
          icon={Cable}
          to={gridEdgeHref}
          label={gridDir === "export" ? t("monitor.flow.gridExporting") : gridDir === "import" ? t("monitor.flow.gridImporting") : t("monitor.flow.gridIdle")}
          tone={gridDir === "idle" ? "muted" : gridDir}
          magnitude={snap.grid.active_W}
          rows={[
            [t("monitor.flow.reactive"), `${signedKW(snap.grid.reactive_VAr)} kVAr`,
              <HelpHint title={g.reactive.term}>{g.reactive.def}</HelpHint>],
            [t("monitor.flow.frequency"), formatHz(snap.grid.frequency_Hz)],
            [t("monitor.flow.voltage"), formatV(snap.grid.voltage_V)],
          ]}
        />
        {hasPv && snap.pv && (
          <FlowEdge
            icon={Sun}
            to={pvEdgeHref}
            label={t("monitor.flow.pvToBus")}
            tone="solar"
            magnitude={snap.pv.active_W}
            unsigned
            rows={[
              [t("monitor.flow.today"), `${snap.pv.today_kWh.toFixed(0)} kWh`],
              [t("monitor.flow.rated"), `${snap.pv.rated_kWp.toFixed(0)} kWp`],
              [t("monitor.flow.capacity"), `${Math.round((snap.pv.active_W / Math.max(1, snap.pv.rated_kWp * 1000)) * 100)}%`],
            ]}
          />
        )}
        <FlowEdge
          icon={BatteryCharging}
          to={bessEdgeHref}
          label={t("monitor.flow.busBess")}
          tone={bessDirection(snap.bess_aggregate.active_W) === "discharge" ? "discharge" : "charge"}
          magnitude={snap.bess_aggregate.active_W}
          rows={[
            [t("monitor.flow.socAvg"), `${snap.bess_aggregate.soc_avg_pct.toFixed(0)}%`],
            [t("monitor.flow.online"), `${snap.bess_aggregate.online_count}/${topology.bess.length}`],
            [t("monitor.flow.capacity"), `${snap.bess_aggregate.total_capacity_kWh.toFixed(0)} kWh`],
          ]}
        />
        <FlowEdge
          icon={House}
          to={loadsEdgeHref}
          label={loadDir === "export" ? t("monitor.flow.loadsSurplus") : loadDir === "import" ? t("monitor.flow.busToLoads") : t("monitor.flow.loadsIdle")}
          tone={loadDir === "export" ? "export" : "muted"}
          magnitude={snap.building_load_W}
          rows={[
            [t("monitor.flow.selfSuffToday"), `${snap.energy_today.self_sufficiency_pct.toFixed(0)}%`,
              <HelpHint title={g.selfSufficiency.term}>{g.selfSufficiency.def}</HelpHint>],
            [t("monitor.flow.selfConsToday"), `${snap.energy_today.self_consumption_pct.toFixed(0)}%`,
              <HelpHint title={g.selfConsumption.term}>{g.selfConsumption.def}</HelpHint>],
            [t("monitor.flow.netAtMeter"), `${signedKW(snap.net_at_meter_W)} kW`],
          ]}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle hint={<GranBadge live label={t("monitor.badge.last60Uplink")} />}>{t("monitor.flowPage.last60Title")}</CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            {/* N30 — the page's single sign explainer (now includes the +/- key),
                always visible and allowed to wrap on the narrow iframe. */}
            <span className="text-[10px] text-text-muted">{t("monitor.flowPage.signNote")}</span>
            <Link
              to={openInHistoricalHref}
              title={t("monitor.flowPage.openInHistoricalTitle")}
              className="flex h-7 shrink-0 items-center gap-1 rounded-md border border-edge bg-surface px-2 text-[11px] font-medium text-text-muted transition hover:border-accent hover:text-accent"
            >
              {t("monitor.openInHistorical")} <ArrowRight size={11} />
            </Link>
          </div>
        </CardHeader>
        <div className="p-3">
          <PowerSparkChart snapshots={recent} hasPv={hasPv} height={280} />
        </div>
      </Card>
    </PageShell>
  );
}

function FlowEdge({
  icon: Icon, label, tone, magnitude, rows, unsigned = false, to,
}: {
  icon: any;
  label: string;
  tone: "import" | "export" | "charge" | "discharge" | "solar" | "muted";
  magnitude: number;
  /** [label, value, optional glossary HelpHint] (CLAR-08). */
  rows: [string, string, ReactNode?][];
  unsigned?: boolean;
  /** N20 — drill target (mirrors the SiteFlowDiagram node above). When set the
   *  whole card becomes a Link; absent leaves the card inert (no false
   *  affordance). Built with carryTo at the call site so the window/env ride. */
  to?: string;
}) {
  // Unified idle noise floor (see format.ts:IDLE_DEADBAND_W) — sub-noise
  // readings must not light the card up as active flow.
  const isActive = !isIdlePower(magnitude);
  const colorClass = {
    import: "text-import border-import/30",
    export: "text-export border-export/30",
    charge: "text-charge border-charge/30",
    discharge: "text-discharge border-discharge/30",
    solar: "text-solar border-solar/30",
    muted: "text-text border-edge",
  }[tone];
  // Static map (DSGN-14): Tailwind's scanner can't see `bg-${tone}/15`.
  const iconBgClass = {
    import: "bg-import/15",
    export: "bg-export/15",
    charge: "bg-charge/15",
    discharge: "bg-discharge/15",
    solar: "bg-solar/15",
    muted: "bg-surface-2",
  }[tone];
  const body = (
    <div className="group flex items-start gap-3 p-4">
      <span className={cn("grid h-10 w-10 place-items-center rounded-md", iconBgClass)}>
        <Icon size={18} className={colorClass.split(" ")[0]} />
      </span>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-wider text-text-faint">{label}</div>
        <div className={cn("text-2xl font-bold leading-none num", isActive ? colorClass.split(" ")[0] : "text-stale")}>
          {unsigned ? unsignedKW(magnitude) : signedKW(magnitude)}<span className="ml-1 text-xs text-text-muted">kW</span>
        </div>
        <div className="mt-2 space-y-0.5 text-[11px] text-text-muted">
          {rows.map(([k, v, hint]) => (
            <div key={k} className="flex items-center justify-between">
              {/* Stop the HelpHint's close-X / backdrop click from bubbling to
                  the card Link — without this, dismissing a help popover would
                  navigate (same guard as RightNowStrip's cells). */}
              <span className="flex items-center gap-1">{k}{hint && <span onClick={(e) => e.stopPropagation()}>{hint}</span>}</span>
              <span className="num text-text">{v}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Drillable cards show a faint hover arrow so the affordance reads;
          inert cards keep the live-flow Activity glyph. */}
      {to
        ? <ArrowRight size={12} className="text-text-faint opacity-0 transition-opacity group-hover:opacity-100" />
        : isActive && <Activity size={12} className={colorClass.split(" ")[0]} />}
    </div>
  );
  // No overflow-hidden here: it would clip the HelpHint popovers that open
  // from the bottom rows.
  return (
    <Card className={cn("card-hover border", colorClass)}>
      {to
        ? <Link to={to} className="block" title={label} aria-label={label}>{body}</Link>
        : body}
    </Card>
  );
}
